// @flow

import ReduceStore from 'PMT/flux/ReduceStore';
import _ from 'lodash';
import moment from 'moment';

import { RouteStore } from 'stores';

import ApiError from 'PMT/entities/ApiError';
import AppDispatcher from 'PMT/flux/AppDispatcher';
import DateUtils from 'PMT/utils/DateUtils';
import SessionUtils from 'PMT/utils/SessionUtils';
import Logger from 'PMT/utils/Logger';

// -- constants
import RoutingEnum from 'constants/RoutingEnum';


import {
  Events,
  ViewConstants,
  ActionTypes,
  Constants
} from 'constants';
import ApiConstants from 'PMT/constants/ApiConstants';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import Timelapse from 'PMT/entities/Timelapse';

import { ProAccount, ProRolesList } from 'PMT/entities/security';

function getTimelapseAtItem(timelapseKey: string,
                            timelapseSelectionKey: string,
                            inFuture: boolean = false):Timelapse {
  const jsonObject = SessionUtils.getItemFromSession(timelapseKey);
  let timelapse:Timelapse = new Timelapse();
  const timelapseSelection = SessionUtils.getIntItemFromSession(
    timelapseSelectionKey,
    ViewConstants.Timelapse.Selection.CUSTOM
  );

  // if null, we create the default values.
  // Use TIMELAPSE_SELECTION to choose the default value.
  // .CUSTOM must be consider a to .MONTH for default date.
  // Note that the default TIMELAPSE_SELECTION is .CUSTOM.
  if (_.isNull(jsonObject) || _.isNull(jsonObject._fromTime) || _.isNull(jsonObject._toTime)) {
    if (_.isNull(jsonObject) || _.isUndefined(jsonObject)) {
      switch (timelapseSelection) {
        case ViewConstants.Timelapse.Selection.DAY:
          timelapse = DateUtils.getTimelapseDayInterval();
          break;
        case ViewConstants.Timelapse.Selection.WEEK:
          if (inFuture) {
            timelapse = DateUtils.getTimelapseWeekIntervalInFuture();
          } else {
            timelapse = DateUtils.getTimelapseWeekInterval();
          }
          break;
        case ViewConstants.Timelapse.Selection.MONTH:
        case ViewConstants.Timelapse.Selection.CUSTOM:
        default:
          if (inFuture) {
            timelapse = DateUtils.getTimelapseMonthIntervalInFuture();
          } else {
            timelapse = DateUtils.getTimelapseMonthInterval();
          }
      }
    }
  } else {
    // If exists, we use the timelapse selection to recreate the object with the good values.
    // If an user is not logout during more than one day, it will result false values without
    // that.
    switch (timelapseSelection) {
      case ViewConstants.Timelapse.Selection.DAY:
        timelapse = DateUtils.getTimelapseDayInterval();
        break;
      case ViewConstants.Timelapse.Selection.WEEK:
        if (inFuture) {
          timelapse = DateUtils.getTimelapseWeekIntervalInFuture();
        } else {
          timelapse = DateUtils.getTimelapseWeekInterval();
        }
        break;
      case ViewConstants.Timelapse.Selection.MONTH:
        if (inFuture) {
          timelapse = DateUtils.getTimelapseMonthIntervalInFuture();
        } else {
          timelapse = DateUtils.getTimelapseMonthInterval();
        }
        break;
      case ViewConstants.Timelapse.Selection.CUSTOM:
      default:
        timelapse.setFromTime(jsonObject._fromTime);
        timelapse.setToTime(jsonObject._toTime);
    }
  }

  return timelapse;
}

class SessionStore extends ReduceStore {

  getInitialState() {
    let authorization = SessionUtils.getItemFromSession(Constants.SessionKey.AUTHORIZATION);

    return {
      /**
       * @type ApiError
       */
      apiError: null,

      /**
       * @type string
       */
      authorization,
    };
  }

  isEmitingCustomEvents(): boolean {
    return true;
  }

  getStateObjectsMap() {
    return {
      apiError: new ApiError(),
    }
  }

  isLoggedIn(): boolean {
    const authorization = this.getAuthorization();
    return !_.isUndefined(authorization) && !_.isNull(authorization);
  }

  //
  // Me account
  //
  saveMeAccount(account: ProAccount) {
    SessionUtils.saveItemToSession(Constants.SessionKey.ACCOUNT, account);
  }

  // -- restaurant and session

  getApiError(): ApiError {
    return this._state.apiError;
  }

  saveRestaurantId(restaurantId: string) {
    SessionUtils.saveItemToSession(Constants.SessionKey.RESTAURANT_ID, restaurantId);
  }

  getRestaurantId(): string {
    return SessionUtils.getItemFromSession(Constants.SessionKey.RESTAURANT_ID);
  }

  saveRestaurantsGroupId(restaurantGroupsId: string) {
    SessionUtils.saveItemToSession(Constants.SessionKey.RESTAURANT_GROUPS_ID, restaurantGroupsId);
  }

  getRestaurantsGroupId(): string {
    return SessionUtils.getItemFromSession(Constants.SessionKey.RESTAURANT_GROUPS_ID);
  }

  saveProDomainId(proDomainId: string) {
    SessionUtils.saveItemToSession(Constants.SessionKey.PRO_DOMAIN_ID, proDomainId);
  }

  getProDomainId(): string {
    return SessionUtils.getItemFromSession(Constants.SessionKey.PRO_DOMAIN_ID);
  }

  saveAuthorization(authorization) {
    SessionUtils.saveObjectToSession(
      Constants.SessionKey.AUTHORIZATION,
      authorization
    );

    this._state.authorization = authorization;
  }

  getAuthorization(): string {
    if (_.isNull(this._state.authorization)) {
      this._state.authorization = SessionUtils.getItemFromSession(Constants.SessionKey.AUTHORIZATION);
    }
    return this._state.authorization;
  }

  // -- Bookings

  // -- booking book date to display

  /**
   * Return the date as milliseconds to display.
   * @returns Number
   */
  getBookingBookDate(): number {
    const date = SessionUtils.getFloatItemFromSession(
      Constants.SessionKey.BOOKING_BOOK_DATE,
      DateUtils.getTodayTimestampAsMilliseconds()
    );
    return date;
  }

  saveBookingBookDate(date: number) {
    SessionUtils.saveItemToSession(Constants.SessionKey.BOOKING_BOOK_DATE, date);
  }

  // -- timelapse

  /**
   *
   * @param selection ViewConstants.Timelapse.Selection
   */
  saveTimelapseSelection(selection: number) {
    SessionUtils.saveItemToSession(Constants.SessionKey.TIMELAPSE_SELECTION, selection);
  }

  /**
   * Default: .CUSTOM
   * @return ViewConstants.Timelapse.Selection
   */
  getTimelapseSelection(): number {
    return SessionUtils.getIntItemFromSession(
      Constants.SessionKey.TIMELAPSE_SELECTION,
      ViewConstants.Timelapse.Selection.CUSTOM
    );
  }

  saveTimelapse(timelapse:Timelapse) {
    SessionUtils.saveItemToSession(Constants.SessionKey.TIMELAPSE, timelapse);
  }

  getTimelapse(): Timelapse {
    return getTimelapseAtItem(
      Constants.SessionKey.TIMELAPSE,
      Constants.SessionKey.TIMELAPSE_SELECTION,
      false
    );
  }

  // -- tabs

  saveEditRestaurantTab(id: number) {
    SessionUtils.saveObjectToSession(Constants.SessionKey.EDIT_RESTAURANT_TAB, id);
  }

  getEditRestaurantTab() {
    return SessionUtils.getIntItemFromSession(Constants.SessionKey.EDIT_RESTAURANT_TAB, 1);
  }

  saveReceiptPageTab(id) {
    SessionUtils.saveObjectToSession(Constants.SessionKey.RECEIPT_PAGE_TAB, id);
  }

  getReceiptPageTab() {
    return SessionUtils.getIntItemFromSession(Constants.SessionKey.RECEIPT_PAGE_TAB, 1);
  }

  saveBookingSettingsTab(id) {
    SessionUtils.saveObjectToSession(Constants.SessionKey.BOOKING_SETTINGS_TAB, id);
  }

  getBookingSettingsTab() {
    return SessionUtils.getIntItemFromSession(Constants.SessionKey.BOOKING_SETTINGS_TAB, 1);
  }

  saveRestaurantMenuTab(id) {
    SessionUtils.saveObjectToSession(Constants.SessionKey.RESTAURANT_MENU_TAB, id);
  }

  getRestaurantMenuTab() {
    return SessionUtils.getIntItemFromSession(Constants.SessionKey.RESTAURANT_MENU_TAB, 1);
  }

  saveLocale(locale: string) {
    SessionUtils.saveObjectToSession(Constants.SessionKey.LOCALE, locale);
  }

  getLocale(): string {
    return SessionUtils.getItemFromSession(Constants.SessionKey.LOCALE, 'fr');
  }

  logout() {
    localStorage.removeItem(Constants.SessionKey.ACCOUNT);
    localStorage.removeItem(Constants.SessionKey.AUTHORIZATION);
    localStorage.removeItem(Constants.SessionKey.RESTAURANT_ID);
    localStorage.removeItem(Constants.SessionKey.RESTAURANT_GROUPS_ID);
    localStorage.removeItem(Constants.SessionKey.PRO_DOMAIN_ID);
    localStorage.removeItem(Constants.SessionKey.BOOKINGS);
  
    // if the user come back after 1 day, the timelapse is not good anymore.
    localStorage.removeItem(Constants.SessionKey.TIMELAPSE);
  
    // if the user come back after 1 day, the booking date is not good anymore.
    localStorage.removeItem(Constants.SessionKey.NEW_BOOKING_DATE);
  
    localStorage.removeItem(Constants.SessionKey.BOOKING_BOOK_DATE);
  }
  

  reduce(store, action, nextState) {
    switch (action.type) {

      // -- login

      case ActionTypes.LOGIN_RESPONSE_SUCCESS:

        this.saveAuthorization(
          `Basic ${btoa(`pro_${action.username}:${action.password}`)}`
        );

        this.emitEvent(Events.ON_LOGIN_SUCCESS);
        return nextState;

      case ActionTypes.LOGIN_RESPONSE_FAILURE:
        if (action.apiError) {
          nextState.apiError = action.apiError;
        }
        this.emitEvent(Events.ON_LOGIN_ERROR);
        return nextState;

      //
      // logout
      //

      case ActionTypes.LOGOUT:
        nextState.authorization = null;
        /**
         * Remove Constants.SessionKey of the user.
         * !!! Warning: You should remove all the keys that are specific to the user.
         */
        this.logout();
        return nextState;

      default:
        return nextState;
    }
  }
}

export default new SessionStore();



// WEBPACK FOOTER //
// ./scripts/stores/SessionStore.js